import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  FileInfo,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CarPurchase, CarPurchaseVersion, UpsertCarPurchase, UpsertCarPurchaseNotification } from "./types";

export const useCarPurchasesApi = () => {
  const api = useApi();

  const getAdminCarPurchase = useCallback(
    (carPurchaseId: string, signal: AbortSignal) => {
      const url = `/admin/car_purchases/${carPurchaseId}`;
      return useSWRMutation<CarPurchase | null, CustomError, string>(url, (url) =>
        api.get<CarPurchase>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminCarPurchases = useCallback(
    <K extends keyof CarPurchase>(
      query: PaginationQueryWithFields<NestedPick<CarPurchase, K>>,
      signal: AbortSignal,
      isArrivingSoon?: boolean,
      hasPermission?: boolean,
    ) => {
      const url = buildUrl<NestedPick<CarPurchase, K>>(
        isArrivingSoon ? "/admin/car_purchases/arriving_soon" : "/admin/car_purchases",
        query,
      );

      const key: string | null = hasPermission === true ? url : null;

      return useSWR<Paginated<NestedPick<CarPurchase, K>> | null, CustomError, string | null>(key, (u) =>
        api.get<Paginated<NestedPick<CarPurchase, K>>>({ url: u!, signal }),
      );
    },
    [api],
  );

  const insertAdminCarPurchase = useCallback(
    async (body: UpsertCarPurchase): Promise<{ id: string }> => {
      const url = `/admin/car_purchases`;
      const id = await api.post<UpsertCarPurchase, { id: string }>(url, body);
      api.mutateCache(["/admin/car_purchases/arriving_soon", url]);
      return id;
    },
    [api],
  );

  const updateAdminCarPurchase = useCallback(
    async (id: string, body: UpsertCarPurchase) => {
      const url = `/admin/car_purchases/${id}`;
      await api.put<UpsertCarPurchase>(url, body);
      api.mutateCache([
        url,
        `/admin/car_purchases`,
        `/admin/car_purchases/${id}/versions`,
        "/admin/car_purchases/arriving_soon",
        "/admin/car_purchases/status",
      ]);
    },
    [api],
  );

  const deleteAdminCarPurchase = useCallback(
    async (carPurchaseId: string): Promise<void> => {
      const url = `/admin/car_purchases/${carPurchaseId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/car_purchases`, `/admin/car_plannings`]);
      });
    },
    [api],
  );

  const getAdminCarPurchaseHistory = useCallback(
    (query: PaginationQuery, carPurchaseID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/car_purchases/${carPurchaseID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCarPurchaseVersion = useCallback(
    (carPurchaseId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/car_purchases/${carPurchaseId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarPurchaseVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarPurchaseVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/car_purchases/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/car_purchases", query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const upsertAdminCarPurchaseNotification = useCallback(
    async (id: string, body: UpsertCarPurchaseNotification) => {
      const url = `/admin/car_purchases/${id}/notification`;
      await api.put<UpsertCarPurchaseNotification>(url, body);
      api.mutateCache([
        url,
        `/admin/car_purchases`,
        `/admin/car_purchases/${id}/versions`,
        `/admin/car_purchases/arriving_soon`,
        `/admin/car_purchases/status`,
      ]);
    },
    [api],
  );

  // const useAdminCarPurchasesStatus = useCallback(
  //   (signal: AbortSignal, load: boolean) => {
  //     const url = load ? "/admin/car_purchases/status" : null;
  //     return useSWRMutation<CarPurchaseStatus | null, CustomError, string | null>(url, (url) =>
  //       api.get<CarPurchaseStatus>({ url, signal }),
  //     );
  //   },
  //   [api],
  // );

  return {
    updateAdminCarPurchase,
    insertAdminCarPurchase,
    useAdminCarPurchases,
    getAdminCarPurchase,
    deleteAdminCarPurchase,
    getAdminCarPurchaseHistory,
    getAdminCarPurchaseVersion,
    getFieldFilters,
    exportExcel,
    upsertAdminCarPurchaseNotification,
    // useAdminCarPurchasesStatus,
  };
};
